import { create } from "zustand";
import { replaceEqualDeep } from "@tanstack/react-query";
import type { ChannelMessage, DirectMessage } from "@atrium/shared";
import { queryClient, type Infinite } from "../../lib/queryClient";

export interface MessagePage<M> {
  items: M[];
  hasMoreBefore: boolean;
  hasMoreAfter?: boolean;
}
type AnyMsg = (ChannelMessage | DirectMessage) & { nonce?: string; pending?: boolean; failed?: boolean };
type Pages<M> = Infinite<MessagePage<M>>;

/*
 * Chat cache: an infinite query per channel / conversation, pages[0] newest,
 * each page's items oldest → newest. Every write goes through here so that
 * ordering, de-duplication and optimistic copies behave the same whether a
 * message arrives from the socket, an HTTP response or a refetch.
 *
 * Ordering rule: confirmed messages sort by id (server-issued UUIDv7, so
 * chronological); local optimistic copies ("pending-<nonce>") sit after all
 * confirmed messages, in the order they were sent.
 */

const isLocal = (m: { id: string }) => m.id.startsWith("pending-");

function compare(a: AnyMsg, b: AnyMsg): number {
  const la = isLocal(a);
  const lb = isLocal(b);
  if (la !== lb) return la ? 1 : -1;
  const ka = la ? (a.nonce ?? a.id) : a.id;
  const kb = lb ? (b.nonce ?? b.id) : b.id;
  return ka < kb ? -1 : ka > kb ? 1 : 0;
}

/** Stable React key: the client nonce survives the optimistic → confirmed swap, so the row never remounts. */
export function messageKey(m: { id: string; nonce?: string }): string {
  return m.nonce ?? m.id;
}

/** Recently deleted ids, so a late or replayed event can't bring a message back. */
const tombstones = new Map<string, number>();
const TOMBSTONE_MS = 10 * 60_000;
function tombstone(id: string) {
  const now = Date.now();
  tombstones.set(id, now);
  if (tombstones.size > 500) for (const [k, t] of tombstones) if (now - t > TOMBSTONE_MS) tombstones.delete(k);
}
const isDeleted = (id: string) => {
  const t = tombstones.get(id);
  return t !== undefined && Date.now() - t < TOMBSTONE_MS;
};

/** Oldest confirmed id held by a page, if any. */
const oldestConfirmed = (p: MessagePage<AnyMsg>) => p.items.find((m) => !isLocal(m))?.id;
const newestConfirmed = (p: MessagePage<AnyMsg>) => p.items.findLast((m) => !isLocal(m))?.id;

/**
 * Put a message in the page whose range it belongs to. Messages older than
 * everything loaded are dropped when more history exists; they'll arrive in
 * order when that page is fetched.
 */
function place<M extends AnyMsg>(pages: MessagePage<M>[], msg: M) {
  let target = -1;
  if (isLocal(msg)) target = 0;
  else {
    for (let i = 0; i < pages.length; i++) {
      const oldest = oldestConfirmed(pages[i]);
      if (oldest === undefined ? i === 0 && pages.length === 1 : msg.id >= oldest) {
        target = i;
        break;
      }
    }
    if (target === -1 && !pages.at(-1)?.hasMoreBefore) target = pages.length - 1;
  }
  if (target === -1) return;
  const items = [...pages[target].items, msg].sort(compare);
  pages[target] = { ...pages[target], items };
}

/** Insert or reconcile a message, replacing its optimistic copy (matched by nonce) and any duplicate. */
export function upsertMessage<M extends AnyMsg>(key: unknown[], msg: M) {
  if (!isLocal(msg) && isDeleted(msg.id)) return;
  queryClient.setQueryData<Pages<M>>(key, (old) => {
    if (!old || !old.pages.length) return old;
    let keptNonce: string | undefined;
    const pages = old.pages.map((p) => {
      if (!p.items.some((m) => m.id === msg.id || (msg.nonce && m.nonce === msg.nonce))) return p;
      return {
        ...p,
        items: p.items.filter((m) => {
          const match = m.id === msg.id || (!!msg.nonce && m.nonce === msg.nonce);
          if (match && m.nonce) keptNonce ??= m.nonce;
          return !match;
        }),
      };
    });
    const next: M = isLocal(msg) ? msg : { ...msg, nonce: msg.nonce ?? keptNonce, pending: false, failed: false };
    place(pages, next);
    return { ...old, pages };
  });
}

export function patchMessage<M extends AnyMsg>(key: unknown[], id: string, fn: (m: M) => M) {
  queryClient.setQueryData<Pages<M>>(key, (old) =>
    old
      ? {
          ...old,
          pages: old.pages.map((p) =>
            p.items.some((m) => m.id === id)
              ? {
                  ...p,
                  items: p.items.map((m) => {
                    if (m.id !== id) return m;
                    const n = fn(m);
                    // Server payloads don't carry the nonce; keep it so the row's key stays stable.
                    return n.nonce || !m.nonce ? n : { ...n, nonce: m.nonce };
                  }),
                }
              : p,
          ),
        }
      : old,
  );
}

export function removeMessage<M extends AnyMsg>(key: unknown[], id: string) {
  if (!isLocal({ id })) tombstone(id);
  queryClient.setQueryData<Pages<M>>(key, (old) =>
    old ? { ...old, pages: old.pages.map((p) => (p.items.some((m) => m.id === id) ? { ...p, items: p.items.filter((m) => m.id !== id) } : p)) } : old,
  );
}

/** The cached copy of a message, if it's loaded. */
export function findMessage<M extends AnyMsg>(key: unknown[], id: string): M | undefined {
  return queryClient.getQueryData<Pages<M>>(key)?.pages.flatMap((p) => p.items).find((m) => m.id === id);
}

/** Put back a message that was removed locally (undo), lifting its tombstone. */
export function restoreMessage<M extends AnyMsg>(key: unknown[], msg: M) {
  tombstones.delete(msg.id);
  upsertMessage(key, msg);
}

/**
 * Merge a fetched result into what's on screen. A (re)fetch is a snapshot from
 * when the request started, so without this anything that landed meanwhile —
 * socket messages, unsent optimistic copies — would vanish until the next
 * event, then pop back in at the wrong place.
 */
export function mergeMessagePages<M extends AnyMsg>(old: Pages<M> | undefined, next: Pages<M>): Pages<M> {
  if (!old?.pages.length || !next.pages.length) return next;
  const oldItems = old.pages.flatMap((p) => p.items);
  const nonceById = new Map<string, string>();
  for (const m of oldItems) if (m.nonce && !isLocal(m)) nonceById.set(m.id, m.nonce);

  const pages = next.pages.map((p) => ({
    ...p,
    items: p.items.filter((m) => !isDeleted(m.id)).map((m) => (!m.nonce && nonceById.has(m.id) ? { ...m, nonce: nonceById.get(m.id) } : m)),
  }));
  const fetchedIds = new Set(pages.flatMap((p) => p.items.map((m) => m.id)));
  const fetchedNonces = new Set(pages.flatMap((p) => p.items.map((m) => m.nonce).filter(Boolean)));
  const newest = newestConfirmed(pages[0]);
  // Only messages newer than the snapshot are carried over; older ones missing from it were deleted.
  const carry = oldItems.filter((m) =>
    isLocal(m) ? !fetchedNonces.has(m.nonce) : !fetchedIds.has(m.id) && !isDeleted(m.id) && (newest === undefined || m.id > newest),
  );
  if (carry.length) pages[0] = { ...pages[0], items: [...pages[0].items, ...carry].sort(compare) };
  return { ...next, pages };
}

/** Shared options for chat infinite queries. */
export const chatQueryOptions = {
  staleTime: Infinity,
  structuralSharing: (old: unknown, next: unknown) => replaceEqualDeep(old, mergeMessagePages(old as Pages<AnyMsg> | undefined, next as Pages<AnyMsg>)),
} as const;

/** Which channel / conversation is on screen, so arriving messages don't mark them unread. */
interface FocusState {
  channelId: string | null;
  conversationId: string | null;
  setChannel: (id: string | null) => void;
  setConversation: (id: string | null) => void;
}
export const useChatFocus = create<FocusState>((set) => ({
  channelId: null,
  conversationId: null,
  setChannel: (channelId) => set({ channelId }),
  setConversation: (conversationId) => set({ conversationId }),
}));

/** Typing indicators: key → (userId → {name, expires}). */
interface TypingState {
  typing: Record<string, Record<string, { name: string; until: number }>>;
  add: (key: string, userId: string, name: string) => void;
  clear: (key: string, userId: string) => void;
}
export const useTyping = create<TypingState>((set, get) => ({
  typing: {},
  add: (key, userId, name) => {
    set({ typing: { ...get().typing, [key]: { ...get().typing[key], [userId]: { name, until: Date.now() + 6000 } } } });
    setTimeout(() => {
      const cur = get().typing[key]?.[userId];
      if (cur && cur.until <= Date.now()) get().clear(key, userId);
    }, 6100);
  },
  clear: (key, userId) => {
    const t = { ...get().typing[key] };
    delete t[userId];
    set({ typing: { ...get().typing, [key]: t } });
  },
}));
